<script lang="ts">
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import { UrlUtil, type NavLink } from '$lib/utils/url.util';
	import type { ClassValue } from 'svelte/elements';
	import IconArrowRight from '~icons/mdi/arrow-right';
	import IconMapMarker from '~icons/mdi/map-marker';

	type ContextLink = {
		label: string;
		nav: NavLink;
		isSamePageLink: boolean;
	};

	const {
		label,
		location,
		additionalContextLinks = [],
		className
	}: {
		label?: string;
		location: PlaceLocation;
		additionalContextLinks?: ContextLink[];
		className?: ClassValue;
	} = $props();

	const placeContextLinks = $derived<ContextLink[]>([
		{
			label: `Full ${location.city.label} Guide`,
			nav: UrlUtil.toCity({ location }),
			isSamePageLink: false
		},
		...additionalContextLinks
	]);
</script>

<aside
	aria-label="Trip planning shortcuts"
	class="card gap-4 bg-base-200 p-4 shadow-sm backdrop-blur-md transition-all card-border sm:flex-row sm:items-center sm:justify-between sm:px-8 sm:py-4 {className}"
>
	<div class="flex items-center gap-3">
		<div class="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
			<IconMapMarker class="text-lg" />
		</div>
		<div class="flex flex-col">
			<span class="text-sm font-bold tracking-widest text-primary">AREA GUIDE</span>
			<p class="text-md font-medium text-base-content/70">
				Planning your route around <strong class="font-bold text-base-content">{label}</strong>?
			</p>
		</div>
	</div>

	<nav aria-label={`${location.city.label} shortcuts`} class="flex w-fit flex-wrap items-center gap-2 sm:w-auto">
		{#each placeContextLinks as context (context.label)}
			<a
				href={`/${context.nav.href}`}
				onclick={(e) => {
					const [targetPath, hash] = context.nav.href.split('#');
					if (hash) {
						const currentPath = window.location.pathname.replace(/^\/+|\/+$/g, '');
						const normalizedTargetPath = targetPath.replace(/^\/+|\/+$/g, '');

						if (currentPath === normalizedTargetPath) {
							e.preventDefault();
							document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
						}
					}
				}}
				class="group btn rounded-full btn-soft font-mono shadow-sm btn-primary btn-sm sm:w-auto"
				data-sveltekit-preload-data={context.nav.preloadData}
				data-sveltekit-preload-code={context.nav.preloadCode}
			>
				<span>{context.label}</span>
				{#if !context.isSamePageLink}
					<IconArrowRight class="transition-transform duration-200 group-hover:translate-x-0.5" />
				{/if}
			</a>
		{/each}
	</nav>
</aside>
